import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Eye, EyeOff, Loader2, Building2, User, Shield, Mail, Lock, ArrowLeft, Fingerprint, ChevronRight, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AUTH_ONBOARDING_RESUME_KEY } from '@/constants/onboardingResume';
import {
  CUSTOMER_ACTIVE_CONTEXT_KEY,
  CUSTOMER_LOGIN_ACCOUNT_TYPE_KEY,
  CUSTOMER_LOGIN_TYPE_MISMATCH_KEY,
} from '@/constants/customerAuthRouting';
import paynovusLogomark from '@/assets/paynovus-logomark.svg';

type AccountType = 'individual' | 'corporate';
type View = 'login' | 'forgot-password';
type ResetStep = 'email' | 'otp' | 'new-password';
const ACTIVE_STATUSES = ['approved', 'active'];
const PENDING_PROFILE_STATUSES = ['pending', 'pending_review', 'review', 'submitted'];
const AUTH_PROPAGATION_ATTEMPTS = 10;
const AUTH_PROPAGATION_DELAY_MS = 300;

export default function CustomerLogin() {
  const { signIn, signOut } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<View>('login');
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const savedEmail = localStorage.getItem('customer.rememberEmail') || '';
  const savedPassword = localStorage.getItem('customer.rememberPassword') || '';
  const savedRemember = localStorage.getItem('customer.rememberMe') === 'true';
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState(savedPassword);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(savedRemember);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetStep, setResetStep] = useState<ResetStep>('email');
  const [crossTypeWarning, setCrossTypeWarning] = useState<string | null>(null);
  const [bypassCrossTypeCheck, setBypassCrossTypeCheck] = useState(false);

  const waitForSessionUser = async (expectedUserId?: string) => {
    for (let attempt = 0; attempt < AUTH_PROPAGATION_ATTEMPTS; attempt++) {
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUser = sessionData?.session?.user ?? null;

      if (sessionUser && (!expectedUserId || sessionUser.id === expectedUserId)) {
        return sessionUser;
      }

      await new Promise(resolve => setTimeout(resolve, AUTH_PROPAGATION_DELAY_MS));
    }

    return null;
  };

  // Pick up error from navigation state (e.g. after type mismatch redirect)
  useEffect(() => {
    const navError = (location.state as any)?.error;
    if (navError) {
      setError(navError);
      // Clear state so it doesn't persist on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const validateSelectedAccountType = async (userId: string, desiredType: AccountType, userEmail?: string | null) => {
    const normalizedUserEmail = userEmail?.toLowerCase();
    const expectedClientType = desiredType === 'corporate' ? 'corporate' : 'individual';

    for (let attempt = 0; attempt < AUTH_PROPAGATION_ATTEMPTS; attempt++) {
      const [{ data: ownedProfiles }, { data: memberRows }] = await Promise.all([
        supabase
          .from('client_profiles')
          .select('id, client_type, status')
          .eq('user_id', userId),
        supabase
          .from('corporate_members')
          .select('client_id')
          .eq('user_id', userId)
          .eq('is_active', true),
      ]);

      const memberClientIds = (memberRows || [])
        .map((row) => row.client_id)
        .filter((id) => !(ownedProfiles || []).some((profile) => profile.id === id));

      let memberProfiles: Array<{ id: string; client_type: string; status: string }> = [];
      if (memberClientIds.length > 0) {
        const { data } = await supabase
          .from('client_profiles')
          .select('id, client_type, status')
          .in('id', memberClientIds);
        memberProfiles = data || [];
      }

      const accessibleProfiles = [...(ownedProfiles || []), ...memberProfiles];
      const hasDirectAccess = accessibleProfiles.some((profile) => (
        profile.client_type === expectedClientType &&
        (ACTIVE_STATUSES.includes(profile.status) || PENDING_PROFILE_STATUSES.includes(profile.status))
      ));

      if (hasDirectAccess) return true;

      if (expectedClientType === 'corporate' && normalizedUserEmail) {
        const { data: openInvites } = await supabase
          .from('corporate_invitations')
          .select('id')
          .eq('email', normalizedUserEmail)
          .in('status', ['pending', 'in_progress', 'accepted'] as any)
          .limit(1);

        if (openInvites && openInvites.length > 0) return true;
      }

      if (attempt < AUTH_PROPAGATION_ATTEMPTS - 1) {
        await new Promise(resolve => setTimeout(resolve, AUTH_PROPAGATION_DELAY_MS));
      }
    }

    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountType) {
      setError('Please select an account type');
      return;
    }
    setLoading(true);
    setError('');
    setCrossTypeWarning(null);

    const normalizedEmail = email.toLowerCase();

    // ── Pre-login cross-type check ──
    // Warn the user if the email already has an active profile of a DIFFERENT type
    if (!bypassCrossTypeCheck) {
      try {
        const { data: existingProfiles } = await supabase
          .from('client_profiles')
          .select('id, user_id, client_type, status')
          .eq('email', normalizedEmail)
          .eq('status', 'approved');

        if (existingProfiles && existingProfiles.length > 0) {
          const selectedType = accountType === 'corporate' ? 'corporate' : 'individual';
          const hasExactMatch = existingProfiles.some(p => p.client_type === selectedType);
          const hasDifferentType = existingProfiles.some(p => p.client_type !== selectedType);

          if (!hasExactMatch && hasDifferentType) {
            // Before warning, check if user has a corporate invitation or membership
            if (selectedType === 'corporate') {
              const [{ data: invites }, { data: members }] = await Promise.all([
                supabase.from('corporate_invitations').select('id').eq('email', normalizedEmail).in('status', ['pending', 'in_progress', 'accepted'] as any).limit(1),
                supabase.from('corporate_members').select('id').eq('user_id', existingProfiles[0]?.user_id || '').eq('is_active', true).limit(1),
              ]);
              if ((invites && invites.length > 0) || (members && members.length > 0)) {
                // Has corporate access via invitation/membership — skip warning
              } else {
                const existingType = existingProfiles[0].client_type === 'corporate' ? 'Business' : 'Personal';
                const selectedLabel = accountType === 'corporate' ? 'Business' : 'Personal';
                setCrossTypeWarning(
                  `This email is already linked to an active ${existingType} account. You selected ${selectedLabel}. If you have both account types, please proceed. Otherwise, go back and select the correct type.`
                );
                setLoading(false);
                return;
              }
            } else {
              const existingType = existingProfiles[0].client_type === 'corporate' ? 'Business' : 'Personal';
              const selectedLabel = accountType === 'corporate' ? 'Business' : 'Personal';
              setCrossTypeWarning(
                `This email is already linked to an active ${existingType} account. You selected ${selectedLabel}. If you have both account types, please proceed. Otherwise, go back and select the correct type.`
              );
              setLoading(false);
              return;
            }
          }
        }
      } catch {
        // Best effort — if the check fails, allow login to continue
      }
    }

    // Reset bypass flag after use
    setBypassCrossTypeCheck(false);

    // Store the selected account type so the auth context picks the right profile
    localStorage.setItem(CUSTOMER_LOGIN_ACCOUNT_TYPE_KEY, accountType === 'corporate' ? 'corporate' : 'individual');
    localStorage.removeItem(CUSTOMER_ACTIVE_CONTEXT_KEY);
    localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);

    const { error: loginError } = await signIn(normalizedEmail, password);

    if (loginError) {
      const msg = (loginError as Error)?.message?.toLowerCase() ?? '';
      if (msg.includes('invalid login credentials') || msg.includes('invalid email or password')) {
        const { data: resumeResult, error: resumeError } = await supabase.functions.invoke('verify-onboarding-password', {
          body: { email: normalizedEmail, password, account_type: accountType }
        });

        if (!resumeError && resumeResult?.success) {
          // Password was synced to auth — retry normal login
          if (resumeResult.password_synced) {
            const { error: retryError } = await signIn(normalizedEmail, password);
            if (!retryError) {
              const currentUser = await waitForSessionUser();
              if (currentUser && accountType) {
                const valid = await validateSelectedAccountType(currentUser.id, accountType, currentUser.email);
                if (!valid) { setLoading(false); return; }
              }
              setLoading(false);
              navigate('/app', { replace: true });
              return;
            }
            // If retry still fails, fall through to generic error
            setError('Login failed after credential update. Please try again.');
            setLoading(false);
            return;
          }

          // Check if the onboarding session is already completed/approved —
          // if so, the user already has an account and should NOT be sent
          // back to onboarding.
          const sessionStatus = resumeResult.session_status || resumeResult.status;
          const isTerminalStep = ['success', 'corporate-success'].includes(resumeResult.current_step);
          const isCompletedSession = ['approved', 'completed'].includes(sessionStatus);

          if (isTerminalStep || isCompletedSession) {
            setError('Your account has been approved but your login credentials need to be updated. Please use "Forgot Password" to reset your password.');
            setLoading(false);
            return;
          }

          sessionStorage.setItem(AUTH_ONBOARDING_RESUME_KEY, JSON.stringify({
            currentStep: resumeResult.current_step,
            accountType: resumeResult.account_type,
            onboardingData: resumeResult.onboarding_data,
            sessionToken: resumeResult.session_token,
            email: normalizedEmail,
          }));
          setLoading(false);
          navigate('/app/onboard', { replace: true });
          return;
        }

        // If account is already approved, show helpful message instead of generic error
        if (!resumeError && resumeResult?.account_approved) {
          setError('Your account has been approved but your login credentials need to be updated. Please use "Forgot Password" below to reset your password.');
          setLoading(false);
          return;
        }

        setError('Invalid email or password. Please try again.');
      } else {
        setError((loginError as Error)?.message || 'Login failed');
      }
      setLoading(false);
      return;
    }

    try {
      const currentUser = await waitForSessionUser();
      if (currentUser) {
        // Auto-finalize any incomplete corporate invitations (create member record if missing)
        if (accountType === 'corporate') {
          try {
            const userEmail = currentUser.email?.toLowerCase();
            if (userEmail) {
              const { data: openInvites } = await supabase
                .from('corporate_invitations')
                .select('id, client_id, role, display_name, job_title, invited_by, status')
                .eq('email', userEmail)
                .in('status', ['pending', 'in_progress', 'accepted'] as any);

              if (openInvites && openInvites.length > 0) {
                for (const inv of openInvites) {
                  // Check if member record already exists
                  const { data: existingMember } = await supabase
                    .from('corporate_members')
                    .select('id')
                    .eq('client_id', inv.client_id)
                    .eq('user_id', currentUser.id)
                    .maybeSingle();

                  if (!existingMember) {
                    // Check if user is already verified (has approved individual profile)
                    const { data: verifiedProfile } = await supabase
                      .from('client_profiles')
                      .select('id, kyc_status')
                      .eq('user_id', currentUser.id)
                      .in('kyc_status', ['approved', 'verified'] as any)
                      .maybeSingle();

                    const kycStatus = verifiedProfile ? 'approved' : 'pending';

                    const validRoles = ['owner', 'admin', 'member'] as const;
                    const invRole = validRoles.includes(inv.role as any) ? inv.role as typeof validRoles[number] : 'member';

                    await supabase.from('corporate_members').insert({
                      client_id: inv.client_id,
                      user_id: currentUser.id,
                      role: invRole,
                      display_name: inv.display_name,
                      job_title: inv.job_title,
                      invited_by: inv.invited_by,
                      kyc_status: kycStatus,
                      is_active: true,
                    });

                    // Mark invitation as accepted
                    if (inv.status !== 'accepted') {
                      await supabase
                        .from('corporate_invitations')
                        .update({ status: 'accepted', accepted_at: new Date().toISOString() })
                        .eq('id', inv.id);
                    }
                    console.log(`[login] Auto-finalized corporate invitation ${inv.id} for user ${currentUser.id}`);
                  }
                }
              }
            }
          } catch (err) {
            console.warn('[login] Auto-finalization best-effort failed:', err);
          }
        }

        const hasMatchingAccount = await validateSelectedAccountType(currentUser.id, accountType, currentUser.email);
        if (!hasMatchingAccount) {
          const label = accountType === 'corporate' ? 'Business' : 'Personal';
          await signOut();
          setError(`No ${label} account found for this email. Please select the correct account type or contact support.`);
          setLoading(false);
          return;
        }
      } else {
        setError('Session could not be established. Please try again.');
        setLoading(false);
        return;
      }
    } catch {
      // Best effort only — if validation cannot run, continue to MFA.
    }


    // Remember me — saves email + password; MFA always mandatory regardless
    if (rememberMe) {
      localStorage.setItem('customer.rememberMe', 'true');
      localStorage.setItem('customer.rememberEmail', email);
      localStorage.setItem('customer.rememberPassword', password);
    } else {
      localStorage.removeItem('customer.rememberMe');
      localStorage.removeItem('customer.rememberEmail');
      localStorage.removeItem('customer.rememberPassword');
    }



    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const verifiedFactor = data?.totp?.find(f => f.status === 'verified');

      // Check if login type mismatch occurred (e.g. selected Business but only Individual profile exists)
      const mismatch = localStorage.getItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
      if (mismatch) {
        localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
        await signOut();
        const label = mismatch === 'corporate' ? 'Business' : 'Personal';
        setError(`No ${label} account found for this email. Please select the correct account type or contact support.`);
        setLoading(false);
        return;
      }

      if (!verifiedFactor) {
        setLoading(false);
        navigate('/app/mfa-setup');
        return;
      }

      setLoading(false);
      navigate('/app/mfa-verify');
    } catch {
      setLoading(false);
      setError('We could not complete the sign-in check. Please try again.');
    }
  };

  // Step 1: Send password reset link via email
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address'); return; }
    setLoading(true);
    setError('');
    try {
      // Use the backend reset flow so delivery stays consistent with the customer portal
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          mode: 'link',
          redirectTo: `${window.location.origin}/app/reset-password`,
        }),
      }).catch(() => null);
      // Ignore rate-limit errors — always advance (prevents enumeration)
      if (res && !res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg: string = data?.error ?? '';
        if (!msg.toLowerCase().includes('security') && !msg.toLowerCase().includes('rate')) {
          setError('Failed to send reset link. Please try again.');
          setLoading(false);
          return;
        }
      }
    } finally {
      setResetStep('otp'); // "otp" step repurposed as "check your email" confirmation
      setLoading(false);
    }
  };


  if (view === 'forgot-password') {
    return (
      <div className="min-h-screen-safe hero-dark flex items-center justify-center p-4 sm:p-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/[0.03] rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-accent/[0.02] rounded-full blur-[80px]" />
        </div>

        <div className="w-full max-w-[420px] relative z-10 animate-fade-in">
          <div className="rounded-3xl border border-border/40 bg-gradient-to-b from-card/90 to-card/70 backdrop-blur-2xl shadow-2xl overflow-hidden">
            <div className="p-8 space-y-6">

              {/* Header */}
              <div className="text-center space-y-3">
                <img src={paynovusLogomark} alt="PayNovus" className="h-16 w-auto mx-auto" />
                {/* Step indicators — 2 steps: email → check inbox */}
                <div className="flex items-center justify-center gap-2">
                  {(['email', 'otp'] as ResetStep[]).map((s, i) => (
                    <div key={s} className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                        resetStep === s ? 'bg-accent text-accent-foreground' :
                        resetStep === 'otp' && s === 'email' ? 'bg-accent/30 text-accent' : 'bg-secondary/60 text-muted-foreground'
                      }`}>
                        {resetStep === 'otp' && s === 'email' ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                      </div>
                      {i < 1 && <div className={`w-6 h-0.5 rounded-full transition-all ${resetStep === 'otp' ? 'bg-accent/50' : 'bg-border/40'}`} />}
                    </div>
                  ))}
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-foreground">
                    {resetStep === 'email' ? 'Reset Your Password' : 'Check Your Email'}
                  </h1>
                  <p className="text-xs text-muted-foreground mt-1">
                    {resetStep === 'email'
                      ? 'Enter your registered email and we\'ll send you a reset link.'
                      : `We sent a password reset link to ${email}.`}
                  </p>
                </div>
              </div>

              {/* Step 1: Email input */}
              {resetStep === 'email' && (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Email Address</label>
                    <div className="relative group">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 group-focus-within:text-accent transition-colors" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        placeholder="your@email.com"
                        required
                        className="pl-10 h-11 bg-background/60 border-border/40 rounded-xl text-sm focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 transition-all"
                      />
                    </div>
                  </div>
                  {error && <div className="text-xs text-destructive bg-destructive/10 rounded-xl p-3 border border-destructive/20">{error}</div>}
                  <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl text-sm font-semibold">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Link'}
                  </Button>
                </form>
              )}

              {/* Step 2: Check your inbox confirmation */}
              {resetStep === 'otp' && (
                <div className="space-y-4 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto">
                    <Mail className="w-7 h-7 text-accent" />
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-sm text-foreground font-medium">Reset link sent!</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Click the link in your email to set a new password. Check your spam folder if you don't see it within a few minutes.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setResetStep('email'); setError(''); }}
                    className="text-xs text-accent hover:underline"
                  >
                    Didn't receive it? Try again
                  </button>
                </div>
              )}

              {/* Back link */}
              {resetStep === 'email' && (
                <button
                  onClick={() => { setView('login'); setError(''); setResetStep('email'); }}
                  className="flex items-center gap-1 mx-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe hero-dark flex items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      {/* Background ambient effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-accent/[0.04] rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-accent/[0.02] rounded-full blur-[100px]" />
        <div className="absolute top-1/2 right-[-5%] w-[300px] h-[300px] bg-cyan/[0.02] rounded-full blur-[80px]" />
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: 'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="w-full max-w-[420px] relative z-10 animate-fade-in">
        {/* Main card */}
        <div className="rounded-3xl border border-border/40 bg-gradient-to-b from-card/90 to-card/70 backdrop-blur-2xl shadow-2xl overflow-hidden">

          {/* Logo & title */}
          <div className="text-center px-8 pt-8 pb-2">
            <img src={paynovusLogomark} alt="PayNovus" className="h-28 w-auto mx-auto mb-4 drop-shadow-[0_0_20px_hsl(var(--accent)/0.4)]" />
            <h1 className="text-xl font-bold text-foreground tracking-tight">Welcome Back</h1>
            <p className="text-xs text-muted-foreground mt-1">Secure access to your financial dashboard</p>
          </div>

          <div className="px-8 pb-8 pt-4 space-y-5">
            {/* Account type selector */}
            <div className="space-y-2">
              <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Select Account</label>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { type: 'individual' as AccountType, icon: User, label: 'Personal', desc: 'Individual account' },
                  { type: 'corporate' as AccountType, icon: Building2, label: 'Business', desc: 'Corporate account' },
                ].map(({ type, icon: Icon, label, desc }) => {
                  const active = accountType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => { setAccountType(type); setError(''); }}
                      className={`group relative flex items-center gap-3 p-3.5 rounded-xl border transition-all duration-200 text-left ${
                        active
                          ? 'border-accent/60 bg-accent/[0.08] shadow-[0_0_24px_hsl(84_81%_44%/0.1)]'
                          : 'border-border/30 bg-background/40 hover:border-border/60 hover:bg-background/60'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                        active ? 'bg-accent/20 text-accent' : 'bg-secondary/60 text-muted-foreground group-hover:text-foreground'
                      }`}>
                        <Icon className="w-4.5 h-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold leading-tight ${active ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'}`}>
                          {label}
                        </p>
                        <p className="text-[10px] text-muted-foreground/70 leading-tight mt-0.5">{desc}</p>
                      </div>
                      {active && (
                        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-accent shadow-[0_0_6px_hsl(84_81%_44%/0.5)]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3.5">
              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Email</label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 group-focus-within:text-accent transition-colors" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="your@email.com"
                    required
                    autoComplete="email"
                    id="login-email"
                    name="email"
                    className="pl-10 h-11 bg-background/60 border-border/40 rounded-xl text-sm focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 transition-all"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">Password</label>
                  <button
                    type="button"
                    onClick={() => { setView('forgot-password'); setError(''); }}
                    className="text-[11px] text-accent/80 hover:text-accent font-medium transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60 group-focus-within:text-accent transition-colors" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="••••••••••••"
                    required
                    autoComplete="current-password"
                    id="login-password"
                    name="password"
                    className="pl-10 pr-11 h-11 bg-background/60 border-border/40 rounded-xl text-sm focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-secondary/60 transition-colors"
                  >
                    {showPassword
                      ? <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />
                      : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div
                role="checkbox"
                aria-checked={rememberMe}
                tabIndex={0}
                className="flex items-center gap-2 pt-0.5 cursor-pointer select-none"
                onClick={() => setRememberMe(prev => !prev)}
                onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setRememberMe(prev => !prev); } }}
              >
                <div className={`h-4 w-4 rounded-[5px] border flex items-center justify-center shrink-0 ${rememberMe ? 'bg-accent border-accent' : 'border-border/50'}`}>
                  {rememberMe && (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 text-accent-foreground"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  Remember me
                </span>
              </div>


              {crossTypeWarning ? (
                <div className="rounded-xl border border-warning/30 bg-warning/10 p-4 animate-fade-in space-y-3">
                  <div className="flex gap-3">
                    <Shield className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-warning mb-1">Account Type Mismatch</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{crossTypeWarning}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl text-xs"
                      onClick={() => {
                        setCrossTypeWarning(null);
                        setAccountType(null);
                      }}
                    >
                      <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                      Change Type
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="rounded-xl text-xs"
                      onClick={() => {
                        setCrossTypeWarning(null);
                        setBypassCrossTypeCheck(true);
                        // Re-submit the form programmatically
                        const form = document.querySelector('form');
                        if (form) form.requestSubmit();
                      }}
                    >
                      Proceed Anyway
                    </Button>
                  </div>
                </div>
              ) : null}

              {error === '__unfinished_application__' ? (
                <div className="text-xs bg-accent/10 rounded-xl p-4 border border-accent/20 animate-fade-in text-center space-y-2.5">
                  <p className="text-foreground font-semibold text-sm">Unfinished Application</p>
                  <p className="text-muted-foreground leading-relaxed">
                    You have an application in progress. Please complete it to activate your account.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-xl text-xs font-semibold"
                    onClick={() => navigate('/app/onboard')}
                  >
                    Continue Application
                    <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              ) : error === '__under_review__' ? (
                <div className="rounded-xl border border-warning/30 bg-warning/10 p-4 flex gap-3 animate-fade-in">
                  <Clock className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-warning mb-1">Application Under Review</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Your application is currently being reviewed by our compliance team. This may take a few hours. You'll be notified once it's complete.
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-xs text-destructive bg-destructive/10 rounded-xl p-3 border border-destructive/20 animate-fade-in">
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={loading || !accountType}
                className="w-full h-11 rounded-xl text-sm font-semibold shadow-lg shadow-accent/15 hover:shadow-accent/25 transition-all group"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Sign In Securely
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border/30" /></div>
              <div className="relative flex justify-center">
                <span className="px-3 text-[10px] text-muted-foreground/60 bg-card/80 uppercase tracking-widest">Protected by</span>
              </div>
            </div>

            {/* Security badges */}
            <div className="flex items-center justify-center gap-5">
               {[
                 { icon: Shield, label: '256-bit TLS' },
                 { icon: Fingerprint, label: 'TOTP 2FA' },
                 { icon: Lock, label: 'EEA Regulated' },
               ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <Icon className="w-3 h-3 text-accent/50" />
                  <span className="text-[10px] text-muted-foreground/60 font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Get Started button */}
        <div className="text-center mt-5">
          <button
            onClick={() => {
              localStorage.removeItem('paynovus_onboarding_progress');
              window.location.href = '/app/onboard';
            }}
            className="inline-flex items-center justify-center px-5 py-2 rounded-lg border border-accent/40 bg-accent/10 text-[11px] font-semibold tracking-widest text-accent hover:bg-accent/20 hover:border-accent/60 transition-all uppercase"
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
}
